import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, HeartHandshake, School, Scale, ClipboardList, Users, Sparkles, ArrowRight } from "lucide-react";
import { FOTO_DANIELA, LIBRO, WHATSAPP } from "@/lib/api";

const SERVICIOS = [
  { icon: ClipboardList, t: "Evaluación psicopedagógica", d: "Proceso diagnóstico integral con técnicas específicas y devolución profesional." },
  { icon: Brain, t: "Intervención psicopedagógica", d: "Tratamiento sostenido orientado al aprendizaje y las funciones cognitivas." },
  { icon: School, t: "Acompañamiento educativo", d: "Trabajo articulado con la trayectoria escolar del paciente." },
  { icon: HeartHandshake, t: "Orientación a familias", d: "Herramientas concretas para sostener el proceso en el hogar." },
  { icon: Users, t: "Orientación a instituciones", d: "Asesoramiento a escuelas y equipos interdisciplinarios." },
  { icon: Sparkles, t: "Psicopedagogía clínica", d: "Abordaje clínico del aprender desde una mirada singular." },
  { icon: Scale, t: "Psicopedagogía forense", d: "Evaluaciones e informes en contextos judiciales." },
];

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.7 } };

export default function Home() {
  return (
    <div data-testid="home-page">
      <section className="grain relative overflow-hidden border-b border-[#dcd5ce]">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 py-24 lg:grid-cols-[1.1fr_0.9fr] lg:py-32">
          <div className="fade-up">
            <p className="label-xs">Necochea · Buenos Aires · Argentina</p>
            <h1 className="mt-6 text-5xl leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
              Un espacio donde el<br />
              <span className="italic text-[#c5a059]">aprender</span> encuentra<br />su propio camino
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-[#4a4744]">
              Nilo Espacio Psicopedagógico acompaña procesos de aprendizaje desde una mirada clínica,
              neurocientífica y profundamente humana. A cargo de la Lic. Daniela Roppel.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contacto" data-testid="hero-turno-btn" className="btn-gold rounded-full px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.18em]">
                Solicitar un turno
              </Link>
              <Link to="/servicios" data-testid="hero-servicios-btn" className="btn-outline-ink rounded-full px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.18em]">
                Ver servicios
              </Link>
            </div>
            <div className="mt-14 flex gap-10 border-t border-[#dcd5ce] pt-8">
              {[["Clínica", "Enfoque"], ["Presencial y virtual", "Modalidad"], ["Forense", "Especialidad"]].map(([a, b]) => (
                <div key={b}>
                  <p className="serif text-2xl">{a}</p>
                  <p className="label-xs mt-1">{b}</p>
                </div>
              ))}
            </div>
          </div>
          <motion.div {...fade} className="relative">
            <div className="absolute -left-6 -top-6 h-40 w-40 rounded-full border border-[#c5a059]/40" />
            <img src={FOTO_DANIELA} alt="Lic. Daniela Roppel" className="relative w-full max-w-md object-cover shadow-[0_30px_80px_-40px_rgba(42,40,38,.5)]" />
            <div className="absolute -bottom-6 right-4 max-w-[15rem] border border-[#dcd5ce] bg-white p-5">
              <p className="serif text-xl">Lic. Daniela Roppel</p>
              <p className="label-xs mt-1">Licenciada en Psicopedagogía</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24">
        <motion.div {...fade}>
          <p className="label-xs">Servicios</p>
          <h2 className="mt-4 max-w-2xl text-4xl tracking-tight sm:text-5xl">Acompañamiento profesional en cada etapa del proceso</h2>
        </motion.div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICIOS.map((s, i) => (
            <motion.div key={s.t} {...fade} transition={{ duration: 0.6, delay: i * 0.06 }}
              className={`card-nilo p-8 ${i === 6 ? "lg:col-span-2 bg-[#f4f1ec]" : ""}`} data-testid={`servicio-card-${i}`}>
              <s.icon size={22} className="text-[#c5a059]" strokeWidth={1.5} />
              <h3 className="mt-6 text-2xl">{s.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#5e5a56]">{s.d}</p>
              {i === 6 && (
                <Link to="/psicopedagogia-forense" className="mt-6 inline-flex items-center gap-2 text-sm text-[#c5a059]">
                  Área exclusiva forense <ArrowRight size={15} />
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-[#dcd5ce] bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 py-24 lg:grid-cols-2">
          <motion.img {...fade} src={LIBRO} alt="Estudio de casos" className="w-full max-w-sm" />
          <motion.div {...fade}>
            <p className="label-xs">Producción profesional</p>
            <h2 className="mt-4 text-4xl tracking-tight sm:text-5xl">Estudio de casos en clínica psicopedagógica</h2>
            <p className="mt-6 max-w-lg leading-relaxed text-[#4a4744]">
              Material de autoría propia donde se articulan la práctica clínica, la lectura teórica y la experiencia
              en el trabajo con niños, adolescentes y familias.
            </p>
            <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="libro-consultar" className="mt-8 inline-block btn-outline-ink rounded-full px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.18em]">
              Consultar disponibilidad
            </a>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-24 text-center">
        <motion.div {...fade}>
          <p className="label-xs">Portal Paciente</p>
          <h2 className="mt-4 text-4xl tracking-tight sm:text-5xl">Tu proceso, siempre disponible</h2>
          <p className="mx-auto mt-6 max-w-2xl leading-relaxed text-[#4a4744]">
            Cada paciente accede a un espacio privado con sus informes, materiales de trabajo, consentimientos
            firmados y el seguimiento de su evolución.
          </p>
          <Link to="/portal" data-testid="cta-portal" className="mt-10 inline-block btn-gold rounded-full px-10 py-4 text-xs font-semibold uppercase tracking-[0.18em]">
            Ingresar al portal
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
